import 'objectFitPolyfill';
import React, {
  useContext, useEffect,
  useState, useMemo,
} from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { MEETING_APP_URL, NETWORKING_LOUNGE_API_URL } from 'appenv';
import { Table } from 'models/networking_lounge/types';
import { selectLocale } from 'models/localization';
import { Button, useMediaQuery, withStyles } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import CloseIcon from '@material-ui/icons/Close';
import { UserSessionContext } from 'components/UserSession';
import CircularLoadingIndicator from 'components/indicators/CircularLoadingIndicator';
import GroupsIcon from 'components/icons/Groups';
import DoneIcon from 'components/icons/Done';
import PeopleIcon from 'components/icons/People';

interface PropsStyle {
  tableStatus: string;
}

const TableItemContainer = styled.div<{ active?: boolean }>`
  height: 220px;
  width: 330px;
  padding: 16px;
  margin: 8px 16px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background-color: #FFF;
  align-self: initial;
  text-decoration: none;
  color: inherit;
  justify-content: space-between;
  opacity: ${({ active }) => (active ? 1 : 0.8)};

  @media (max-width: 360px) {
    max-width: 100%;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 20px;
  line-height: 32px;
  font-weight: 800;
`;

const TotalCountContainer = styled.div<{ tableStatus?: string }>`
  display: flex;
  font-size: 15px;
  letter-spacing: 0.5px;
  color:
    ${({ tableStatus }) => {
    if (tableStatus === 'in_progress') {
      return '#EAB53E';
    }
    if (tableStatus === 'full') {
      return '#A2AAB3';
    }
    return '#06AF97';
  }};

  & > svg {
    margin-right: 1px;
  }
`;

const TopicContainer = styled.div`
  font-size: 20px;
  line-height: 24px;
  text-align: center;
  font-weight: 700;
`;

const ActionContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ActionButton = withStyles(() => ({
  root: (props: PropsStyle) => ({
    backgroundColor: `${props.tableStatus === 'in_progress' ? '#EAB53E' : '#06AF97'}`,
    color: '#FFF',
    width: '100%',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: `${props.tableStatus === 'in_progress' ? '#EAB53E' : '#06AF97'}`,
      color: '#FFF',
    },
    '&:disabled': {
      backgroundColor: '#A2AAB3',
      color: '#FFF',
    },
  }),
}))(Button);

const StyledButton = withStyles({
  root: {
    backgroundColor: '#E2654D',
    color: '#FFF',
    width: '100%',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#E2654D',
      color: '#FFF',
    },
  },
})(Button);

const JoinNowButton = withStyles({
  root: {
    backgroundColor: '#06AF97',
    color: '#FFF',
    width: '100%',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '24px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#06AF97',
      color: '#FFF',
    },
  },
})(Button);

const DesktopDialogContent = withStyles({
  root: {
    width: '490px',
    paddingBottom: '24px',
    textAlign: 'center',
  },
})(DialogContent);

const MobileDialogContent = withStyles({
  root: {
    paddingBottom: '24px',
    textAlign: 'center',
  },
})(DialogContent);

const ModalTitle = styled.h4`
  text-align: center;
  font-size: 20px;
  line-height: 24px;
  font-weight: 700;
`;

const StyledCloseIcon = styled(CloseIcon)`
  position: absolute;
  top: 16px;
  right: 16px;
  cursor: pointer;
`;

declare global {
  interface Window {
    objectFitPolyfill: () => unknown;
  }
}

const tableStatusColorCode = (tableStatus) => {
  if (tableStatus === 'in_progress') {
    return '#EAB53E';
  }
  if (tableStatus === 'full') {
    return '#A2AAB3';
  }
  return '#06AF97';
};

const TableItem = ({
  table: {
    id,
    topic,
    status,
    activeUserCount,
    maxCapacity,
  },
  index,
}: {
  table: Table;
  index: number;
}) => {
  const { t } = useTranslation();
  const locale = useSelector(selectLocale);
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const DialogContentContainer = useMemo(() => (isDesktop ? DesktopDialogContent : MobileDialogContent), [isDesktop]);
  const { userSession } = useContext(UserSessionContext);
  const {
    fullName: userName,
    authToken: userEmsUniqueToken,
    email: userEmail,
    company: userCompany,
    title: userJobPosition,
  } = userSession ?? {};

  const [showJoinDiscussionModal, setShowJoinDiscussionModal] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinUrl, setJoinUrl] = useState('');
  const [errorType, setErrorType] = useState('');

  const hideJoinDiscussionModal = () => {
    setShowJoinDiscussionModal(false);
  };

  const onStartDiscussionBtnClick = async (tableId) => {
    setJoining(true);
    setShowJoinDiscussionModal(true);
    try {
      const { data } = await axios.post(`${NETWORKING_LOUNGE_API_URL}/tables/${tableId}/join`, {
        userName,
        userEmail,
        userCompany,
        userEmsUniqueToken,
        userJobPosition,
      });
      setJoining(false);
      if (data.participant) {
        const { meetingId, id: participantId } = data.participant;
        const meetingUrl = `${MEETING_APP_URL}/?meetingId=${meetingId}&participantId=${participantId}&locale=${locale}`;
        setJoinUrl(meetingUrl);
        window.open(meetingUrl, '_blank');
      }
    } catch (error) {
      const { code } = error.response.data;
      if (code === 'ERR_VEXPO_TABLE_IS_FULL') {
        setJoining(false);
        setErrorType('full');
      }
    }
  };

  const onJoinNowBtnClick = () => {
    window.open(joinUrl, '_blank');
  };

  useEffect(() => {
    window.objectFitPolyfill();
  });

  return (
    <TableItemContainer
      active={status !== 'full'}
    >
      <TitleContainer>
        <div>{`${index + 1}.`}</div>
        <TotalCountContainer tableStatus={status}>
          <PeopleIcon color={tableStatusColorCode(status)} width="32px" height="32px" />
          <div>{`${activeUserCount}/${maxCapacity}`}</div>
        </TotalCountContainer>
      </TitleContainer>
      <TopicContainer>{topic}</TopicContainer>
      <ActionContainer>
        <ActionButton
          onClick={() => onStartDiscussionBtnClick(id)}
          tableStatus={status}
          disabled={status === 'full'}
        >
          {status === 'pending' && t('networking_lounge.start_discussion', 'Start Discussion')}
          {status === 'in_progress' && t('networking_lounge.join_discussion', 'Join Discussion')}
          {status === 'full' && t('networking_lounge.full', 'Full')}
        </ActionButton>
      </ActionContainer>
      <Dialog
        open={showJoinDiscussionModal}
        onClose={hideJoinDiscussionModal}
        disableBackdropClick={joining}
        aria-describedby="alert-dialog-description"
      >
        <DialogContentContainer>
          {joining && (
            <>
              <CircularLoadingIndicator />
              <DialogContentText>
                {t('networking_lounge.joining_discussion_table', 'Joining the discussion table...')}
              </DialogContentText>
            </>
          )}
          {
            !joining && errorType && (
              <>
                <StyledCloseIcon onClick={hideJoinDiscussionModal} />
                <GroupsIcon color="#E2654D" width="96px" height="96px" />
                <ModalTitle>
                  {t('networking_lounge.full_warning_modal.title', 'Oops, A Step Slower!')}
                </ModalTitle>
                <DialogContentText>
                  {t('networking_lounge.full_warning_modal.description', 'We have reached the maximum number of participants, please join other discussion topics or come back later.')}
                </DialogContentText>
                <StyledButton onClick={hideJoinDiscussionModal}>
                  {t('networking_lounge.full_warning_modal.i_understand', 'I understand')}
                </StyledButton>
              </>
            )
          }
          {
            !joining && joinUrl.length > 0 && (
              <>
                <StyledCloseIcon onClick={hideJoinDiscussionModal} />
                <DoneIcon color="#06AF97" width="96px" height="96px" />
                <ModalTitle>
                  {t('networking_lounge.success_modal.title', 'All set!')}
                </ModalTitle>
                <DialogContentText>
                  {t('networking_lounge.success_modal.description', 'If a new tab is not open, please click the button below to join the discussion table.')}
                </DialogContentText>
                <JoinNowButton onClick={onJoinNowBtnClick}>
                  {t('networking_lounge.success_modal.join_now', 'Join now')}
                </JoinNowButton>
              </>
            )
          }
        </DialogContentContainer>
      </Dialog>
    </TableItemContainer>
  );
};

export default React.memo(TableItem);
